/**
 * Account Billing > Payment Methods: the master record of a customer's credit card / direct debit details.
 *
 * Methods are immutable: staff can add a new one (it becomes active, the previous one inactive) and
 * admins can reveal or delete them, but nothing can be edited. Deleting wipes the full details and
 * leaves e.g. "Visa ending 4242". Full numbers are only shown on an explicit, audited admin reveal.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Timer as TimerIcon,
  Visibility as RevealIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface PaymentMethod {
  id: string;
  method_type: 'credit_card' | 'direct_debit';
  status: 'pending' | 'active' | 'inactive' | 'deleted';
  label: string;
  display_name: string | null;
  expiry_month: number | null;
  expiry_year: number | null;
  bsb: string | null;
  source: 'order' | 'portal' | 'admin';
  source_order_id: string | null;
  source_order_reference?: string | null;
  details_on_file: boolean;
  cvv_on_file: boolean;
  cvv_expires_at: string | null;
  created_at: string | null;
  created_by: string | null;
  deleted_at: string | null;
  deleted_by: string | null;
}

interface Revealed {
  credit_card?: { card_number: string; expiry_month: number; expiry_year: number; cvv?: string; name_on_card: string } | null;
  direct_debit?: { bsb: string; account_number: string; account_name: string } | null;
}

interface AuditRow {
  id: string;
  action: string;
  payment_method: string | null;
  performed_by: string | null;
  ip_address: string | null;
  notes: string | null;
  performed_at: string;
}

const REVEAL_SECONDS = 60;
const SOURCE_LABEL: Record<string, string> = { order: 'Order', portal: 'Customer portal', admin: 'Staff' };
const STATUS_COLOR: Record<string, 'success' | 'default' | 'error' | 'warning'> = {
  active: 'success', inactive: 'default', deleted: 'error', pending: 'warning',
};

const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtDateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-AU') : '—');
const apiMessage = (e: any, fallback: string) => {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};
const onlyDigits = (v: string, max: number) => v.replace(/\D/g, '').slice(0, max);

// ----------------------------------------------------------------------------- add dialog

const AddDialog: React.FC<{ accountId: string; onClose: (added: boolean) => void }> = ({ accountId, onClose }) => {
  const [type, setType] = useState<'credit_card' | 'direct_debit'>('credit_card');
  const [card, setCard] = useState({ number: '', month: '', year: '', cvv: '', name: '' });
  const [bank, setBank] = useState({ bsb: '', account: '', name: '' });
  const [terms, setTerms] = useState<{ credit_card_terms: string; direct_debit_terms: string } | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiService.adminGetEzidebitPaymentTerms().then(setTerms).catch(() => setTerms({ credit_card_terms: '', direct_debit_terms: '' }));
  }, []);
  useEffect(() => setAgreed(false), [type]);

  const activeTerms = terms ? (type === 'credit_card' ? terms.credit_card_terms : terms.direct_debit_terms) : '';

  const save = async () => {
    setError(null);
    setBusy(true);
    try {
      await apiService.addAccountPaymentMethod(
        accountId,
        type === 'credit_card'
          ? { method_type: type, card_number: onlyDigits(card.number, 19), expiry_month: Number(card.month), expiry_year: Number(card.year), cvv: card.cvv, name_on_card: card.name.trim(), terms_accepted: true }
          : { method_type: type, bsb: bank.bsb, account_number: bank.account, account_name: bank.name.trim(), terms_accepted: true }
      );
      onClose(true);
    } catch (e) {
      setError(apiMessage(e, 'Could not add the payment method.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => !busy && onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Add payment method</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          The new method becomes the active one and the current one becomes inactive. It can't be edited afterwards.
          The customer is emailed a confirmation with the Ezidebit terms, and a task is created to update Emersion.
        </Alert>
        <RadioGroup row value={type} onChange={(e) => setType(e.target.value as 'credit_card' | 'direct_debit')} sx={{ mb: 2 }}>
          <FormControlLabel value="credit_card" control={<Radio />} label="Credit card" />
          <FormControlLabel value="direct_debit" control={<Radio />} label="Bank account (direct debit)" />
        </RadioGroup>

        {type === 'credit_card' ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Card number" value={card.number} inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
                onChange={(e) => setCard({ ...card, number: onlyDigits(e.target.value, 19).replace(/(.{4})/g, '$1 ').trim() })} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth required label="Expiry month" placeholder="MM" value={card.month} inputProps={{ inputMode: 'numeric' }}
                onChange={(e) => setCard({ ...card, month: onlyDigits(e.target.value, 2) })} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth required label="Expiry year" placeholder="YYYY" value={card.year} inputProps={{ inputMode: 'numeric' }}
                onChange={(e) => setCard({ ...card, year: onlyDigits(e.target.value, 4) })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth required label="CVV" value={card.cvv} inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
                onChange={(e) => setCard({ ...card, cvv: onlyDigits(e.target.value, 4) })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="Name on card" value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth required label="BSB" placeholder="123456" value={bank.bsb} inputProps={{ inputMode: 'numeric' }}
                onChange={(e) => setBank({ ...bank, bsb: onlyDigits(e.target.value, 6) })} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth required label="Account number" value={bank.account} inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
                onChange={(e) => setBank({ ...bank, account: onlyDigits(e.target.value, 10) })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="Account name" value={bank.name} onChange={(e) => setBank({ ...bank, name: e.target.value })} />
            </Grid>
          </Grid>
        )}

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
          {type === 'credit_card' ? 'Credit card' : 'Direct debit'} — Ezidebit terms and conditions
        </Typography>
        <Box sx={{ maxHeight: 200, overflow: 'auto', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
          <Typography component="div" variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
            {terms === null ? 'Loading…' : activeTerms.trim() || 'Terms are not configured (Settings → Ordering). They will be omitted from the email.'}
          </Typography>
        </Box>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
          label="The customer has agreed to these terms and conditions"
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => onClose(false)} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !agreed}>{busy ? 'Adding…' : 'Add payment method'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ----------------------------------------------------------------------------- main tab

const AccountPaymentMethodsTab: React.FC<{ accountId: string }> = ({ accountId }) => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [revealed, setRevealed] = useState<{ id: string; details: Revealed } | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopReveal = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
    setRevealed(null);
    setCountdown(0);
  }, []);
  useEffect(() => stopReveal, [stopReveal]); // never leave full details on screen after leaving the tab

  const load = useCallback(async () => {
    setError(null);
    try {
      setMethods(await apiService.getAccountPaymentMethods(accountId));
      if (isAdmin) setAudit(await apiService.getAccountPaymentAudit(accountId));
    } catch (e) {
      setError(apiMessage(e, 'Could not load payment methods.'));
    } finally {
      setLoading(false);
    }
  }, [accountId, isAdmin]);
  useEffect(() => { load(); }, [load]);

  const reveal = async (m: PaymentMethod) => {
    setBusyId(m.id);
    setError(null);
    try {
      const details = await apiService.revealAccountPaymentMethod(accountId, m.id);
      setRevealed({ id: m.id, details });
      setCountdown(REVEAL_SECONDS);
      if (timer.current) clearInterval(timer.current);
      timer.current = setInterval(() => {
        setCountdown((c) => {
          if (c <= 1) { stopReveal(); return 0; }
          return c - 1;
        });
      }, 1000);
      load(); // the reveal is audited: show it
    } catch (e) {
      setError(apiMessage(e, 'Could not retrieve the details.'));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (m: PaymentMethod) => {
    if (!window.confirm(`Delete the details for ${m.label}? The card/account number and CVV are permanently removed; only the last four digits are kept. This cannot be undone.`)) return;
    setBusyId(m.id);
    setError(null);
    try {
      await apiService.deleteAccountPaymentMethod(accountId, m.id);
      if (revealed?.id === m.id) stopReveal();
      setNotice(`${m.label} was deleted.`);
      await load();
    } catch (e) {
      setError(apiMessage(e, 'Could not delete the payment method.'));
    } finally {
      setBusyId(null);
    }
  };

  const removeCvv = async (m: PaymentMethod) => {
    if (!window.confirm(`Delete the CVV for ${m.label}? Do this once the details have been entered into Emersion.`)) return;
    setBusyId(m.id);
    setError(null);
    try {
      await apiService.deleteAccountPaymentMethodCvv(accountId, m.id);
      if (revealed?.id === m.id) stopReveal();
      setNotice('CVV deleted.');
      await load();
    } catch (e) {
      setError(apiMessage(e, 'Could not delete the CVV.'));
    } finally {
      setBusyId(null);
    }
  };

  const detailLine = (m: PaymentMethod) =>
    [m.display_name, m.method_type === 'credit_card' && m.expiry_month && m.expiry_year ? `Expires ${String(m.expiry_month).padStart(2, '0')}/${m.expiry_year}` : null, m.method_type === 'direct_debit' && m.bsb ? `BSB ${m.bsb}` : null]
      .filter(Boolean).join(' · ');

  const methodMeta = (m: PaymentMethod) => (
    <Typography variant="caption" color="text.secondary" component="div">
      Added {fmtDate(m.created_at)} · {SOURCE_LABEL[m.source] || m.source}
      {m.created_by ? ` (${m.created_by})` : ''}
      {m.source_order_id && (
        <> · Order <Box component={RouterLink} to={`/admin/orders/${m.source_order_id}`} sx={{ color: 'primary.main' }}>{m.source_order_reference || 'view'}</Box></>
      )}
      {m.status === 'deleted' && <> · Details deleted {fmtDate(m.deleted_at)}{m.deleted_by ? ` by ${m.deleted_by}` : ''}</>}
    </Typography>
  );

  const cvvBadge = (m: PaymentMethod) =>
    m.cvv_on_file ? (
      <Chip size="small" color="warning" label={`CVV on file — auto-deletes ${fmtDate(m.cvv_expires_at)}`} sx={{ mt: 0.5 }} />
    ) : null;

  const renderRevealed = (m: PaymentMethod) => {
    if (revealed?.id !== m.id) return null;
    const { credit_card: cc, direct_debit: dd } = revealed.details;
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="warning" icon={<TimerIcon fontSize="inherit" />} sx={{ mb: 1, py: 0.5 }}>
          Details hidden in {countdown}s. Every access is logged.
        </Alert>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {cc && (
            <>
              <Typography variant="body2"><strong>Card number:</strong> {cc.card_number}</Typography>
              <Typography variant="body2"><strong>Expiry:</strong> {String(cc.expiry_month).padStart(2, '0')}/{cc.expiry_year}</Typography>
              <Typography variant="body2"><strong>CVV:</strong> {cc.cvv || 'not on file'}</Typography>
              <Typography variant="body2"><strong>Name on card:</strong> {cc.name_on_card}</Typography>
            </>
          )}
          {dd && (
            <>
              <Typography variant="body2"><strong>BSB:</strong> {dd.bsb}</Typography>
              <Typography variant="body2"><strong>Account number:</strong> {dd.account_number}</Typography>
              <Typography variant="body2"><strong>Account name:</strong> {dd.account_name}</Typography>
            </>
          )}
        </Box>
      </Box>
    );
  };

  const actions = (m: PaymentMethod) =>
    isAdmin && m.status !== 'deleted' ? (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
        {m.details_on_file && (
          <Button size="small" variant="outlined" startIcon={busyId === m.id ? <CircularProgress size={14} /> : <RevealIcon />} onClick={() => reveal(m)} disabled={busyId === m.id}>
            View details
          </Button>
        )}
        {m.cvv_on_file && (
          <Button size="small" variant="outlined" color="warning" onClick={() => removeCvv(m)} disabled={busyId === m.id}>Delete CVV</Button>
        )}
        <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => remove(m)} disabled={busyId === m.id}>Delete details</Button>
      </Box>
    ) : null;

  const active = methods.find((m) => m.status === 'active');
  const pending = methods.filter((m) => m.status === 'pending');
  const history = methods.filter((m) => m.status !== 'active' && m.status !== 'pending');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h6">Payment methods</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setNotice(null); setAdding(true); }}>Add payment method</Button>
      </Box>

      <Alert severity="info" icon={<LockIcon fontSize="inherit" />} sx={{ mb: 2 }}>
        Payment details are encrypted and can't be edited: add a new method to replace the active one.
        {isAdmin ? ' Viewing or deleting details is recorded in the audit log below.' : ' Only admins can view or delete full details.'}
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="overline" color="text.secondary">Active</Typography>
            {active ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6">{active.label}</Typography>
                  <Chip size="small" color="success" label="Active" />
                  <Chip size="small" variant="outlined" label={active.method_type === 'credit_card' ? 'Credit card' : 'Direct debit'} />
                </Box>
                <Typography variant="body2" color="text.secondary">{detailLine(active)}</Typography>
                {methodMeta(active)}
                {cvvBadge(active)}
                {renderRevealed(active)}
                {actions(active)}
              </>
            ) : (
              <Typography color="text.secondary">No active payment method on file.</Typography>
            )}
          </Paper>

          {pending.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {pending.length} payment method{pending.length !== 1 ? 's' : ''} from an unlinked order will appear here once the order is linked to this account.
            </Alert>
          )}

          {history.length > 0 && (
            <Paper variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary" sx={{ px: 2.5, pt: 2, display: 'block' }}>History</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment method</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((m) => (
                      <TableRow key={m.id} sx={{ opacity: m.status === 'deleted' ? 0.75 : 1, verticalAlign: 'top' }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{m.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{detailLine(m)}</Typography>
                          {cvvBadge(m)}
                          {renderRevealed(m)}
                        </TableCell>
                        <TableCell><Chip size="small" color={STATUS_COLOR[m.status]} label={m.status} sx={{ textTransform: 'capitalize' }} /></TableCell>
                        <TableCell>{methodMeta(m)}</TableCell>
                        <TableCell align="right">{actions(m)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {isAdmin && audit.length > 0 && (
            <Paper variant="outlined">
              <Typography variant="overline" color="text.secondary" sx={{ px: 2.5, pt: 2, display: 'block' }}>Audit log</Typography>
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Payment method</TableCell>
                      <TableCell>By</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {audit.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(a.performed_at)}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{a.action.replace(/_/g, ' ')}</TableCell>
                        <TableCell>{a.payment_method || '—'}</TableCell>
                        <TableCell>{a.performed_by || '—'}{a.ip_address ? ` (${a.ip_address})` : ''}</TableCell>
                        <TableCell>{a.notes || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {adding && (
        <AddDialog accountId={accountId} onClose={(added) => { setAdding(false); if (added) { setNotice('Payment method added. The customer has been emailed and a task created to update Emersion.'); load(); } }} />
      )}
    </Box>
  );
};

export default AccountPaymentMethodsTab;
